import { SaveCompliance } from "@services/Compliance/Index.jsx";
import { useForm } from 'react-hook-form';
import ButtonComp from "@components/UI/Button/Index.jsx";
import { tipoComplianceOptions } from "@enums/Compliance/Index.jsx";
import SelectOptionComp from "@components/UI/SelectOption/Index.jsx";
import InputTextComp from "@components/UI/InputText/Index.jsx";
import { useEffect } from "react";
import SweetAlert from "@components/Feedback/SweetAlert/Index.jsx";
import { blockedOptionMessage } from "@utils/Index.jsx";
import { useFetchDataCascade } from "@hooks/api/useFetchDataCascade.js";

const Create = ({ onSave, clearFields }) => {

    const {
        handleSubmit,
        reset,
        watch,
        setValue,
        register,
    } = useForm({
        defaultValues: {
            tipoCompliance: null,
            ano: null,
            servico: null,
            tipo: null,
            nomeProcesso: null,
            descricao: "",
        },
    });

    const {
        yearOptions,
        serviceOptions,
        typeOptions,
        processOptions,
        yearLoading,
        serviceLoading,
        typeLoading,
        processLoading,
        fetchYears,
        fetchServicesByYear,
        fetchTypesByServiceId,
        fetchProcessosByType,
        setServiceOptions,
        setTypeOptions,
        setProcessOptions,
    } = useFetchDataCascade();

    const dataCriacao = new Date().toISOString().split('T')[0];

    const onSubmit = async (data) => {
        try {
            SweetAlert.loading();

            const payload = {
                tipoCompliance: data.tipoCompliance.value,
                idProcesso: data.nomeProcesso.value,
                descricao: data.descricao,
                dataCriacao: dataCriacao,
                flgStatus: true
            };

            const result = await SaveCompliance(payload);

            SweetAlert.success('Cadastro Realizado!', 'Os dados foram salvos com sucesso.');

            onSave({ ...result });

            reset({
                tipoCompliance: null,
                ano: null,
                servico: null,
                tipo: null,
                nomeProcesso: null,
                descricao: "",
            });

        } catch (error) {
            const errorMessage = error.parsed?.message || 'Não foi possível salvar o compliance.';

            SweetAlert.errorHTML('Erro ao cadastrar', errorMessage);
            console.error(error);
        }
    };

    useEffect(() => {
        if (clearFields) {
            reset();
        }
    }, [clearFields, reset]);

    useEffect(() => {
        fetchYears();
    }, []);

    useEffect(() => {
        const selectedYear = watch("ano")?.value;

        if (selectedYear) {
            setValue("servico", null);
            setValue("tipo", null);
            setValue("nomeProcesso", null);
            fetchServicesByYear(selectedYear);
        } else {
            setServiceOptions([]);
            setTypeOptions([]);
            setProcessOptions([]);
            setValue("servico", null);
            setValue("tipo", null);
            setValue("nomeProcesso", null);
        }
    }, [watch("ano")]);

    useEffect(() => {
        const selectedServiceId = watch("servico")?.value;

        if (selectedServiceId) {
            setValue("tipo", null);
            setValue("nomeProcesso", null);
            fetchTypesByServiceId(selectedServiceId);
        } else {
            setTypeOptions([]);
            setProcessOptions([]);
            setValue("tipo", null);
            setValue("nomeProcesso", null);
        }
    }, [watch("servico")]);

    useEffect(() => {
        const selectedTypeId = watch("tipo")?.value;

        if (selectedTypeId) {
            setValue("nomeProcesso", null);
            fetchProcessosByType(selectedTypeId);
        } else {
            setProcessOptions([]);
            setValue("nomeProcesso", null);
        }
    }, [watch("tipo")]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="form">
            <div className='col-md-12'>
                <div className="row">

                    <div className="p-3">
                        <h3> - Observação: Os campos serão habilitados conforme o preenchimento do atual campo habilitado. </h3>
                    </div>

                    {/* Coluna Esquerda */}
                    <div className="col-8 col-md-8">

                        <div className='d-flex'>
                            <div className='col-md-6'>
                                <SelectOptionComp
                                    label="Compliance"
                                    options={tipoComplianceOptions}
                                    value={watch("tipoCompliance")}
                                    onChange={(selected) => setValue("tipoCompliance", selected)}
                                    placeholder="Selecionar"
                                    hoverMessage="Selecionar um compliance"
                                    required
                                />
                            </div>

                            <div className='col-md-6'>
                                <SelectOptionComp
                                    label="Ano"
                                    options={yearOptions}
                                    value={watch("ano")}
                                    onChange={(selected) => setValue("ano", selected)}
                                    placeholder={watch("tipoCompliance") ? "Selecionar" : "Desabilitado"}
                                    hoverMessage={"Selecionar um ano"}
                                    isDisabled={!watch("tipoCompliance")}
                                    isLoading={yearLoading}
                                    required
                                />
                            </div>
                        </div>

                        <SelectOptionComp
                            label="Serviço"
                            options={serviceOptions}
                            value={watch("servico")}
                            onChange={(selected) => setValue("servico", selected)}
                            placeholder={watch("ano") ? "Selecionar um serviço" : blockedOptionMessage}
                            hoverMessage="Selecionar um serviço"
                            isDisabled={!watch("ano")}
                            isLoading={serviceLoading}
                            required
                        />

                        <SelectOptionComp
                            label="Tipo"
                            options={typeOptions}
                            value={watch("tipo")}
                            onChange={(selected) => setValue("tipo", selected)}
                            placeholder={watch("servico") ? "Selecionar um tipo" : blockedOptionMessage}
                            hoverMessage="Selecionar um tipo"
                            isDisabled={!watch("servico")}
                            isLoading={typeLoading}
                            required
                        />

                        <SelectOptionComp
                            label="Processo"
                            options={processOptions}
                            value={watch("nomeProcesso")}
                            onChange={(selected) => setValue("nomeProcesso", selected)}
                            placeholder={watch("tipo") ? "Selecionar um processo" : blockedOptionMessage}
                            hoverMessage="Selecionar um processo"
                            isDisabled={!watch("tipo")}
                            isLoading={processLoading}
                            required
                        />

                        <InputTextComp
                            label={"Descrição"}
                            name={"descricao"}
                            {...register('descricao')}
                            placeholder={watch("nomeProcesso") ? "Preencher com uma descrição" : blockedOptionMessage}
                            hoverMessage={"Preencher com uma descrição"}
                            maxLength={255}
                            disabled={!watch("nomeProcesso")}
                            required={true}
                        />

                    </div>

                    {/* Coluna Direita */}
                    <div className="col-md-4 text-justify lh-lg">
                        <h3 className="py-2"> Este projeto ou serviço será a base para as solicitações ques serão realizadas. </h3>

                        <h3 className="py-2"> Um projeto ou serviço, pode conter múltiplas solicitações. </h3>

                        <h3 className="py-2"> Ao registrar um novo compliance, o sistema criará a estrutura necessária para vincular as solicitações, documentos, etc. </h3>

                        <h3 className="py-2"> O código é gerado automaticamente pelo sistema após o preenchimento dos campos Compliance e Ano.</h3>
                    </div>

                    <div className="col-8 mt-4 px-3">

                        <ButtonComp
                            title={"SALVAR"}
                            type={"submit"}
                            buttonColor={"#2b97be"}
                            classnameIcon={"fa-regular fa-floppy-disk"}
                            hoverMessage={"Botão para confirmar o cadastro"}
                        />

                    </div>

                </div>
            </div>
        </form>
    );
};

export default Create;